import React from 'react';
import {
  DrawerProvider,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  ActionButton,
  Button,
} from 'equal-ds-ui';
import { Edit3, Key, ArrowRightToLine } from 'lucide-react';
import type { AdminUserTableRow, AdminUserPermissions } from '../../../../types/admin-users';
import {
  ACCOUNT_STATUS,
  DISABLED_STATUSES,
  normalizeStatus,
  PROTECTED_ROLES,
} from '../../../../constants/admin-users.constants';

export interface AdminUserDetailPanelProps {
  user: AdminUserTableRow | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (user: AdminUserTableRow) => void;
  onToggleStatus: (user: AdminUserTableRow) => void;
  onResetPassword: (user: AdminUserTableRow) => void;
  permissions: AdminUserPermissions;
}

/** Extract initials from name (up to 2 chars) */
function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

/**
 * Determine the status action config. Status is normalized (uppercase, trimmed) so
 * both disabled spellings (INACTIVE admin-disabled / DEACTIVE auto-lockout) and
 * lowercase 'locked' (MFA lock path) resolve to the correct action — matching
 * AdminUserActionCell so the row action and the detail panel stay in sync.
 */
function getStatusAction(status: string) {
  const normalized = normalizeStatus(status);
  if (normalized === ACCOUNT_STATUS.ACTIVE) {
    return { label: 'De-Activate User', intent: 'warning' as const };
  }
  if (DISABLED_STATUSES.has(normalized)) {
    return { label: 'Activate User', intent: 'approve' as const };
  }
  if (normalized === ACCOUNT_STATUS.LOCKED) {
    return { label: 'Unlock User', intent: 'approve' as const };
  }
  return null;
}

export const AdminUserDetailPanel: React.FC<AdminUserDetailPanelProps> = ({
  user,
  isOpen,
  onClose,
  onEdit,
  onToggleStatus,
  onResetPassword,
  permissions,
}) => {
  if (!user) {
    return null;
  }

  const raw = user.raw;
  const profile = raw?.user_profile;
  const initials = getInitials(user.name || 'U');
  const phone = user.mobile ? `+91 ${user.mobile}` : '--';
  const rawRoleName = (profile?.roles?.role_name ?? '').toLowerCase();
  const isProtectedRole = PROTECTED_ROLES.has(rawRoleName);
  const canResetPassword = permissions.canResetPassword && !isProtectedRole;
  const statusAction = getStatusAction(user.status);
  const isPending = normalizeStatus(user.status) === ACCOUNT_STATUS.PENDING_APPROVAL;

  return (
    <DrawerProvider open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerOverlay />
      <Drawer variant="overlay" width="420px">
        <DrawerContent
          className="equal-ds-root flex flex-col h-full max-h-screen"
          style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F2F8FB 100%)' }}
        >
          {/* Close button */}
          <div className="flex justify-end px-4 pt-4">
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-gray-50 hover:bg-gray-200 transition-colors"
              aria-label="Close"
            >
              <ArrowRightToLine className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* User info header */}
          <div className="px-6 pb-6">
            <div className="flex items-center gap-4 mb-6">
              {/* Avatar */}
              <div className="w-14 h-14 rounded-full bg-background-tertiary border-2 border-border-default flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-semibold text-text-primary">{initials}</span>
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-semibold text-text-primary truncate">
                  {user.name || '--'}
                </h2>
                <p className="text-sm text-text-secondary">{phone}</p>
              </div>
            </div>

            {/* Detail grid */}
            <div className="grid grid-cols-2 gap-y-5 gap-x-6">
              <div>
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-1">Email</p>
                <p className="text-sm font-medium text-text-primary break-all">{user.email || '--'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-1">Employee ID</p>
                <p className="text-sm font-medium text-text-primary">{user.empId || '--'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-1">Department</p>
                <p className="text-sm font-medium text-text-primary">{profile?.division || user.deptCode || '--'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-1">Role</p>
                <p className="text-sm font-medium text-text-primary">{user.roleName || '--'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-1">Created On</p>
                <p className="text-sm font-medium text-text-primary">{user.createdAt || '--'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-1">Approved By</p>
                <p className="text-sm font-medium text-text-primary">{user.createdBy || '--'}</p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-border-default mx-6" />

          {/* Action buttons */}
          <div className="px-6 py-6 flex flex-col gap-3 mt-auto">
            {/* Edit User - Primary full width */}
            {permissions.canEditUser && (
              <Button
                variant="primary"
                onClick={() => { onClose(); onEdit(user); }}
                className="w-full"
              >
                <Edit3 className="w-4 h-4" />
                Edit User
              </Button>
            )}

            {/* Change Password - Secondary full width (non-admin only) */}
            {canResetPassword && (
              <Button
                variant="secondary"
                onClick={() => { onClose(); onResetPassword(user); }}
                className="w-full"
              >
                <Key className="w-4 h-4" />
                Change Password
              </Button>
            )}

            {/* Status Action - ActionButton full width */}
            {permissions.canToggleStatus && statusAction && !isPending && (
              <ActionButton
                intent={statusAction.intent}
                size="md"
                onClick={() => { onClose(); onToggleStatus(user); }}
                className="w-full"
              >
                {statusAction.label}
              </ActionButton>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </DrawerProvider>
  );
};
